import {
  type TemplateVariable,
  TemplateVariableType,
} from "@/shared/domain/agents";

import {
  IMPLICIT_COLLECTION_DESCRIPTION,
  IMPLICIT_COLLECTION_VAR_NAME,
  IMPLICIT_DATASET_DESCRIPTION,
  IMPLICIT_DATASET_VAR_NAME,
  type VariableMeta,
} from "./buildRequestTemplate";
import styles from "./VariablesPanel.module.css";

interface VariablesPanelProps {
  /** Derived variables list — the names and types are auto-detected
   *  from placeholders by `buildVariables`. */
  variables: TemplateVariable[];
  /** User-editable description / default for each variable, keyed by
   *  name. The implicit `dataset.id` row ignores this. */
  variableMeta: Record<string, VariableMeta>;
  /** Patch one variable's metadata. The editor merges the patch into
   *  the existing entry (or creates one if absent). */
  onMetaChange: (name: string, patch: Partial<VariableMeta>) => void;
}

/**
 * Body of the Variables section. Names and types are derived from
 * placeholders the user typed in goal / kickoff fields and are read-only
 * here; descriptions and defaults are editable so the launch page can
 * show useful hints and pre-fill sensible values.
 *
 * The implicit `{{dataset.id}}` row is special: its description is fixed
 * (so the editor isn't asking the user to explain something invariant)
 * and there is no default field (the dataset is always chosen at launch
 * time via the dataset picker).
 */
export function VariablesPanel({
  variables,
  variableMeta,
  onMetaChange,
}: VariablesPanelProps) {
  if (variables.length === 0) {
    return (
      <div className={styles.empty}>
        No variables yet. Reference one anywhere by typing{" "}
        <code>{"{{name}}"}</code>.
      </div>
    );
  }
  return (
    <ul className={styles.list}>
      {variables.map((variable) => {
        const isDatasetImplicit = variable.name === IMPLICIT_DATASET_VAR_NAME;
        const isCollectionImplicit =
          variable.name === IMPLICIT_COLLECTION_VAR_NAME;
        const isImplicit = isDatasetImplicit || isCollectionImplicit;
        const meta = variableMeta[variable.name];
        return (
          <li key={variable.name} className={styles.row}>
            <div className={styles.rowHeader}>
              <span className={styles.name}>{variable.name}</span>
              <span className={styles.typeBadge}>
                {renderTypeLabel(variable.type)}
              </span>
              {isImplicit && (
                <span className={styles.implicitNote}>auto-managed</span>
              )}
            </div>
            {isDatasetImplicit && (
              <p className={styles.implicitDescription}>
                {IMPLICIT_DATASET_DESCRIPTION}
              </p>
            )}
            {isCollectionImplicit && (
              <>
                <p className={styles.implicitDescription}>
                  {IMPLICIT_COLLECTION_DESCRIPTION}
                </p>
                {/* The collection's description is fixed, but the author may
                    pre-fill a default collection id so the launch page
                    doesn't force a picker click every time. */}
                <div className={styles.metaGrid}>
                  <label
                    className={styles.metaLabel}
                    htmlFor={`var-${variable.name}-default`}
                  >
                    Default
                  </label>
                  <input
                    id={`var-${variable.name}-default`}
                    className={styles.metaInput}
                    value={meta?.default ?? ""}
                    placeholder="Optional. Pre-filled on the launch page."
                    onChange={(e) =>
                      onMetaChange(variable.name, { default: e.target.value })
                    }
                  />
                </div>
              </>
            )}
            {!isImplicit && (
              <div className={styles.metaGrid}>
                <label
                  className={styles.metaLabel}
                  htmlFor={`var-${variable.name}-description`}
                >
                  Description
                </label>
                <input
                  id={`var-${variable.name}-description`}
                  className={styles.metaInput}
                  value={meta?.description ?? ""}
                  placeholder="What this variable is for, shown to the launcher."
                  onChange={(e) =>
                    onMetaChange(variable.name, { description: e.target.value })
                  }
                />
                <label
                  className={styles.metaLabel}
                  htmlFor={`var-${variable.name}-default`}
                >
                  Default
                </label>
                <input
                  id={`var-${variable.name}-default`}
                  className={styles.metaInput}
                  value={meta?.default ?? ""}
                  placeholder="Optional. Pre-filled on the launch page."
                  onChange={(e) =>
                    onMetaChange(variable.name, { default: e.target.value })
                  }
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function renderTypeLabel(type: TemplateVariableType): string {
  switch (type) {
    case TemplateVariableType.DATASET_ID:
      return "dataset";
    case TemplateVariableType.COLLECTION_ID:
      return "collection";
    case TemplateVariableType.DEVICE_ID:
      return "device";
    case TemplateVariableType.STRING:
    default:
      return "text";
  }
}
